import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCurrentJustification } from '../services/agreementAccess';
import type { AccessState } from '../types/agreementAccess.types';
import { hasExpired, millisecondsUntilExpiry } from '../utils/accessExpiry';
import { deriveAccessState } from '../utils/accessState';
import { agreementKeys } from './agreementKeys';

/** Spacing of repeat checks when the server still reports live after the local expiry time. */
export const EXPIRY_RECHECK_FLOOR_MS = 15_000;

export function useAgreementAccess(agreementId: string | undefined) {
    const [mountedAt] = useState(() => Date.now());
    const recheckedExpiryRef = useRef<string | null>(null);

    const current = useQuery({
        queryKey: agreementKeys.access(agreementId ?? ''),
        queryFn: () => fetchCurrentJustification(agreementId!),
        enabled: !!agreementId,
        staleTime: 0,
    });
    const { data, dataUpdatedAt, errorUpdatedAt, refetch } = current;
    const justification = data?.justification ?? null;
    const expiresAt = justification?.expiresAt ?? null;

    // The server decides when the window has closed; the timer only asks it again.
    // errorUpdatedAt is a dependency so a failed re-read is followed by another.
    useEffect(() => {
        if (!expiresAt) return;

        const remaining = millisecondsUntilExpiry(expiresAt, Date.now());
        const delay = recheckedExpiryRef.current === expiresAt ? Math.max(remaining, EXPIRY_RECHECK_FLOOR_MS) : remaining;
        const timer = setTimeout(() => {
            recheckedExpiryRef.current = expiresAt;
            void refetch();
        }, delay);

        return () => clearTimeout(timer);
    }, [expiresAt, dataUpdatedAt, errorUpdatedAt, refetch]);

    // A cached answer that lapsed before this mount is not trusted until it is re-read.
    const isCacheLapsed = !!expiresAt && dataUpdatedAt < mountedAt && hasExpired(expiresAt, mountedAt);

    let state: AccessState;
    if (!data || isCacheLapsed) {
        state = current.isError ? 'error' : 'checking';
    } else {
        state = deriveAccessState(data);
    }

    return { state, justification, retry: () => void refetch() };
}
